import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { useSendKBISMutation } from 'api/tunnelSteps';
import { userSelectors } from 'reducers';
import { useTunnelChangeHandlers } from 'utils/hooks';
import { checkAcceptedFileTypes, validateFileSize } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import { TunnelStep, TunnelBox } from '../common';
import { FileErrorMessage, SubmitButton, FileInputButton, ActionButton } from 'components';
import { Typography, Box } from '@mui/material';

const KBIS = props => {
    const { userStep, handleNextStep } = props;

    const [sendKBIS, { isLoading: KBISLoader, isSuccess, reset, isError, error: submitError }] =
        useSendKBISMutation();
    const setTunnelChangesHandlers = useTunnelChangeHandlers();

    const { investorCompany } = useSelector(userSelectors.getInternalUser);

    const isResubmit = useMemo(() => !!userStep, [userStep]);
    const documentName = useMemo(() => investorCompany?.kbis?.name || null, [investorCompany]);

    const [uploadedFile, setUploadedFile] = useState(null);
    const [lastUploadedFileName, setLastUploadedFileName] = useState(documentName);
    const [fileTypeError, setFileTypeError] = useState(false);
    const [fileSizeError, setFileSizeError] = useState(false);

    useEffect(() => {
        setLastUploadedFileName(documentName);
    }, [documentName]);

    const handleFileChange = useCallback(
        ({ target: { files } }) => {
            const file = files[0];
            checkAcceptedFileTypes(
                file,
                () => {
                    setFileTypeError(false);
                    if (validateFileSize(file)) {
                        setFileSizeError(false);
                        setUploadedFile(file);
                        setLastUploadedFileName(file?.name);
                        reset();
                    } else {
                        setFileSizeError(true);
                    }
                },
                () => {
                    setUploadedFile(null);
                    setFileTypeError(true);
                    setFileSizeError(false);
                }
            );
        },
        [reset]
    );

    const handleSubmit = useCallback(
        afterSubmitCallback => () => {
            let formData = new FormData();
            formData.append('file', uploadedFile);
            sendKBIS({ payload: formData, callback: afterSubmitCallback });
        },
        [sendKBIS, uploadedFile]
    );

    useEffect(() => {
        if (isError) {
            if (submitError.status === 415) {
                setUploadedFile(null);
                setFileSizeError(true);
            } else {
                setUploadedFile(null);
                setFileTypeError(true);
            }
        }
    }, [isError, submitError]);

    const checkChanges = useCallback(
        () => !isSuccess && uploadedFile !== null,
        [isSuccess, uploadedFile]
    );

    const changesMade = useMemo(() => checkChanges(), [checkChanges]);

    useEffect(
        () => setTunnelChangesHandlers(handleSubmit, checkChanges, isResubmit),
        [handleSubmit, checkChanges, isResubmit, setTunnelChangesHandlers]
    );

    return (
        <TunnelStep
            title={texts.KBIS}
            subtitle={
                <Typography variant="body1">
                    <i>{texts.DISCONNECT_REMINDER}</i>
                </Typography>
            }
        >
            <TunnelBox>
                <Typography gutterBottom>{texts.MERCI_KBIS}</Typography>
                <Typography gutterBottom>{texts.FERA_OBJET}</Typography>
                <Typography gutterBottom>{texts.REVIENDROS}</Typography>
                <Box pt={3} display="flex" flexWrap="wrap" justifyContent="center">
                    {fileTypeError && <FileErrorMessage variant="type" />}
                    {fileSizeError && <FileErrorMessage variant="size" />}
                    {!fileTypeError && !fileSizeError && (
                        <Typography
                            sx={{ width: '100%', mb: 2, textAlign: 'center' }}
                            variant="body2"
                        >{`${texts.UPLOADED_FILE_NAME} : ${
                            lastUploadedFileName || texts.AUCUN
                        }`}</Typography>
                    )}
                    <FileInputButton
                        id="kbis"
                        showIcon
                        sx={{ width: '45%', display: 'flex', justifyContent: 'center' }}
                        label={texts.UPLOAD_FILE_BUTTON}
                        onFileChange={handleFileChange}
                    />
                    <Box
                        sx={theme => ({
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '45%',
                            [theme.breakpoints.down('sm')]: { width: '100%', marginTop: 3 },
                        })}
                    >
                        <ActionButton
                            variant="contained"
                            sx={{ padding: '10px 16px' }}
                            onClick={handleSubmit(() => handleNextStep(true))}
                            disabled={!uploadedFile || KBISLoader}
                            loading={KBISLoader}
                        >
                            {texts.ENVOYER}
                        </ActionButton>
                    </Box>
                </Box>
            </TunnelBox>
            <SubmitButton
                text={isResubmit && changesMade ? texts.SAVE_AND_CONTINUE : texts.SUIVANT}
                onClick={handleNextStep}
                disabled={
                    (isResubmit && !!uploadedFile && !isSuccess) || (!isResubmit && !isSuccess)
                }
            />
        </TunnelStep>
    );
};

KBIS.propTypes = {
    userStep: PropTypes.object,
    handleNextStep: PropTypes.func,
};

export default KBIS;
